import type { CdekPrintKind } from './cdek-print-agent'
import workerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url'

const PAGE_SIZE: Record<CdekPrintKind, { cssName: string; width: string; height: string }> = {
  a6: { cssName: 'A6', width: '105mm', height: '148mm' },
  a4: { cssName: 'A4', width: '210mm', height: '297mm' },
}

export class CdekBrowserPrintError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CdekBrowserPrintError'
  }
}

/** Render each unchanged PDF page into a PNG for the CSS-sized browser fallback. */
export async function renderCdekPdfPages(pdf: Blob): Promise<readonly string[]> {
  try {
    const pdfjs = await import('pdfjs-dist')
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
    const loadingTask = pdfjs.getDocument({
      data: new Uint8Array(await pdf.arrayBuffer()),
    })
    const document = await loadingTask.promise
    try {
      const pages: string[] = []
      for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
        const page = await document.getPage(pageNumber)
        const viewport = page.getViewport({ scale: 2 })
        const canvas = window.document.createElement('canvas')
        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)
        const context = canvas.getContext('2d')
        if (context === null) throw new CdekBrowserPrintError('Браузер не смог подготовить страницы для печати.')
        await page.render({ canvas, canvasContext: context, viewport }).promise
        pages.push(canvas.toDataURL('image/png'))
        canvas.width = 1
        canvas.height = 1
      }
      return pages
    } finally {
      await loadingTask.destroy()
    }
  } catch (caught) {
    if (caught instanceof CdekBrowserPrintError) throw caught
    throw new CdekBrowserPrintError('Браузер не смог подготовить страницы для печати.')
  }
}

function escapeAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
}

export function cdekBrowserPrintHtml(kind: CdekPrintKind, pages: readonly string[]): string {
  const size = PAGE_SIZE[kind]
  const pageMarkup = pages.map((source, index) => `
    <section class="cdek-page" data-page="${index + 1}">
      <img src="${escapeAttribute(source)}" alt="Страница ${index + 1} ${size.cssName}" />
    </section>`).join('')
  return `<!doctype html>
<html lang="ru">
  <head>
    <meta charset="utf-8" />
    <title>Печать СДЭК ${size.cssName}</title>
    <style>
      @page { size: ${size.cssName}; margin: 0 }
      html, body { margin: 0; padding: 0; background: white }
      .cdek-page {
        width: ${size.width};
        height: ${size.height};
        overflow: hidden;
        break-after: page;
        page-break-after: always;
      }
      .cdek-page:last-child { break-after: auto; page-break-after: auto }
      .cdek-page img { display: block; width: 100%; height: 100%; object-fit: fill }
    </style>
  </head>
  <body>${pageMarkup}</body>
</html>`
}

function waitForFrameLoad(frame: HTMLIFrameElement): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false
    const timer = window.setTimeout(() => {
      if (settled) return
      settled = true
      reject(new CdekBrowserPrintError('Браузер не подготовил страницу для печати вовремя.'))
    }, 5000)
    frame.addEventListener('load', () => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      resolve()
    }, { once: true })
  })
}

async function waitForImages(frame: HTMLIFrameElement): Promise<void> {
  const images = frame.contentDocument ? Array.from(frame.contentDocument.images) : []
  await Promise.all(images.map((image) => {
    if (image.complete) return Promise.resolve()
    return new Promise<void>((resolve, reject) => {
      image.addEventListener('load', () => resolve(), { once: true })
      image.addEventListener('error', () => reject(new CdekBrowserPrintError('Браузер не загрузил страницу для печати.')), { once: true })
    })
  }))
}

/** Print the PNG pages from a hidden same-origin HTML iframe exactly once. */
export async function printCdekBrowserPages(kind: CdekPrintKind, pages: readonly string[]): Promise<void> {
  if (pages.length === 0) throw new CdekBrowserPrintError('В документе нет страниц для печати.')
  const frame = window.document.createElement('iframe')
  frame.title = `Печать ${kind.toUpperCase()}`
  frame.setAttribute('aria-hidden', 'true')
  frame.dataset.cdekPrintFallback = kind
  frame.style.position = 'fixed'
  frame.style.width = '1px'
  frame.style.height = '1px'
  frame.style.right = '0'
  frame.style.bottom = '0'
  frame.style.border = '0'
  frame.style.opacity = '0'
  frame.srcdoc = cdekBrowserPrintHtml(kind, pages)
  window.document.body.append(frame)
  try {
    await waitForFrameLoad(frame)
    await waitForImages(frame)
    const context = frame.contentWindow
    if (context === null) throw new CdekBrowserPrintError('Браузер не открыл страницу для печати.')
    context.focus()
    context.print()
  } finally {
    frame.remove()
  }
}
